import "@/App.css";
import "@/lib/sentry";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { SentryErrorBoundary } from "@/lib/sentry";
import AnalyticsListener from "@/components/AnalyticsListener";
import { AuthProvider } from "@/context/AuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Brands from "@/pages/Brands";
import BrandDetail from "@/pages/BrandDetail";
import Solutions from "@/pages/Solutions";
import Leadership from "@/pages/Leadership";
import Careers from "@/pages/Careers";
import News from "@/pages/News";
import NewsDetail from "@/pages/NewsDetail";
import AdSizes from "@/pages/AdSizes";
import ReservedSizes from "@/pages/ReservedSizes";
import NotFound from "@/pages/NotFound";
import { Privacy, Terms, Cookies } from "@/pages/Legal";
import CookieBanner from "@/components/CookieBanner";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const PublicLayout = () => (
  <SmoothScroll>
    <Layout />
  </SmoothScroll>
);

function App() {
  return (
    <div className="App">
      <SentryErrorBoundary
        fallback={({ resetError }) => (
          <div className="min-h-screen bg-ink text-white flex items-center justify-center p-8">
            <div className="max-w-lg text-center">
              <p className="font-mono text-xs font-extrabold uppercase tracking-widest text-primary">
                Something broke
              </p>
              <h1 className="headline text-5xl md:text-6xl uppercase mt-6">Sorry.</h1>
              <p className="mt-6 text-white/70 leading-relaxed">
                We&rsquo;ve been notified and are looking into it. Please try again — most of the time a reload does the trick.
              </p>
              <button
                onClick={() => { resetError(); window.location.reload(); }}
                className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-3 font-sans text-sm font-bold hover:bg-white hover:text-ink transition-colors"
              >
                Reload the page
              </button>
            </div>
          </div>
        )}
      >
      <HelmetProvider>
        <BrowserRouter>
          <AnalyticsListener />
          <CookieBanner />
        <AuthProvider>
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#111111",
                border: "1px solid #222222",
                color: "#F5F5F5",
                borderRadius: 0,
                fontFamily: "Manrope, sans-serif",
              },
            }}
          />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/brands" element={<Brands />} />
              {/* Legacy slug — kept forever as a 301 for existing links / SEO after the EIU → EIG rename (2026-07-22). */}
              <Route path="/brands/education-intelligence-unit" element={<Navigate to="/brands/education-intelligence-group" replace />} />
              <Route path="/brands/:slug" element={<BrandDetail />} />
              <Route path="/solutions" element={<Solutions />} />
              <Route path="/leadership" element={<Leadership />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<NewsDetail />} />
              <Route path="/advertising" element={<AdSizes />} />
              {/* Unlisted — reserved Jumbo sizes, shared by direct link only */}
              <Route path="/advertising/reserved-sizes" element={<ReservedSizes />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<Cookies />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
      </SentryErrorBoundary>
    </div>
  );
}

export default App;
